import type { GroupBase, OptionsOrGroups, OptionType } from './types'

import { flatten } from 'lodash/fp'

export const isObject = (o: any) =>
	typeof o === 'object' && o !== null && !Array.isArray(o)
export const isPromise = (p: any) => p.then && typeof p.then === 'function'
export const isEmptyString = (str: any) =>
	typeof str === 'string' && str.length === 0

type X = Record<string, any>
type Z = any[]

// Array Helpers
// ------------------------------

export const diffArr = (a: Z, b: Z): Z => a.filter((i) => b.indexOf(i) < 0)

// NOTE: only use `isEqualArr` for primitives (string, number etc.) like "keys"
const stringifyArr = (a: Z): string => a.filter(Boolean).sort().join(',')
export const isEqualArr = (a: Z, b: Z): boolean =>
	stringifyArr(a) === stringifyArr(b)

// Object Helpers
// ------------------------------

type CloneObjOptions = { add?: X; remove?: string }
export const cloneObj = (obj: X, options: CloneObjOptions = {}) => {
	const { add, remove } = options
	// add key/value pair
	if (add) {
		return { ...obj, ...add }
	}

	// remove by key
	if (remove) {
		const n = { ...obj }
		delete n[remove]

		return n
	}

	return { ...obj }
}

export const objectMap = (object: X, mapFn: (a: any, b: string) => any) =>
	Object.keys(object).reduce((res, key) => {
		const result = cloneObj(res)
		const value = mapFn(object[key], key)

		if (value) {
			result[key] = value

			return result
		}

		return res
	}, {})

// String Helpers
// ------------------------------

export const stringCompare = (a: any, b: any): boolean =>
	JSON.stringify(a) === JSON.stringify(b)

export const isGroup = (
	option: OptionType | GroupBase<OptionType>
): option is GroupBase<OptionType> => 'options' in option
export const isGrouped = (
	options: OptionsOrGroups<OptionType, GroupBase<OptionType>>
) => options.some(isGroup)
export const isDataOption = (
	option: OptionType | GroupBase<OptionType>
): option is OptionType => !('options' in option)

export const gatFlatOptions = <TOption extends OptionType>(
	options: OptionsOrGroups<TOption, GroupBase<TOption>>
) =>
	flatten(
		options?.map((v) => {
			if ((v as GroupBase<TOption>).options) {
				return (v as GroupBase<TOption>).options
			} else {
				return [v] as TOption[]
			}
		})
	)
